import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { TextArea } from 'shared/ui/TextArea';
import { Checkbox } from 'shared/ui/Checkbox';
import { showNotification } from 'app/providers/NotificationsProvider';
import { CreateNoteData, Note, NoteType, useCreateNoteMutation, useUpdateNoteMutation } from 'entities/Note';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'shared/ui/DatePicker';
import { CLIENT_DATE_WITH_MINUTE_TIME_FORMAT } from 'shared/utils/constants';
import { Paragraph } from 'shared/ui/Paragraph';
import { getServerDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { DrawerMode } from '../model/types';
import { Select } from 'shared/ui/Select';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { getLoggedUserData, getUserName, useGetPaginatedUsersQuery, User, UserRoles } from 'entities/User';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { MultipleSelect } from 'shared/ui/MultipleSelect';
import { Lead } from 'entities/Lead';
import { chooseUserModalActions, getSelectedUser, UserType } from 'widgets/ChooseUserModal';
import { Input } from 'shared/ui/Input';

interface NoteFormProps {
  mode: Nullable<DrawerMode>;
  onClose: () => void;
  note?: Nullable<Note>;
  lead?: Nullable<Lead>;
  user?: Nullable<User>;
}

export const NoteForm: FC<NoteFormProps> = memo((props) => {
  const { note, mode, lead, user, onClose } = props;
  const { t } = useAppTranslation(['boxes', 'common']);

  const [form] = Form.useForm();

  const [createNote, { isLoading: isCreationLoading }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdationLoading }] = useUpdateNoteMutation();
  const { data: warehouses } = useGetAllWarehousesQuery();
  const { data } = useGetPaginatedUsersQuery({ role: [UserRoles.MANAGER, UserRoles.SUPERUSER] });

  const loggedUser = useAppSelector(getLoggedUserData);

  const selectedUser = useAppSelector(getSelectedUser);

  const isLoading = isUpdationLoading || isCreationLoading;

  const isNoteEditMode = mode === DrawerMode.NOTE_EDIT;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (note) {
      form.setFieldsValue({
        ...note,
        deadlineDate: note?.deadlineDate && dayjs(note.deadlineDate),
        description: null,
        warehouseId: note.warehouse?.warehouseId,
        assignedIds: note.assignedUsers?.map((user) => user.userId),
        userType: note.lead ? UserType.LEAD : note.user ? UserType.USER : null,
      });

      dispatch(chooseUserModalActions.setSelectedUser(note.lead || note.user));
    } else {
      form.resetFields();
    }

    if (lead) {
      form.setFieldsValue({
        userType: UserType.LEAD,
        description: `
        Name: ${lead.firstName || ''} ${lead.lastName || ''} 
        Email: ${lead.email || '-'}
        Phone: ${lead.phone || '-'}
        `,
      });

      dispatch(chooseUserModalActions.setSelectedUser(lead));
    }

    if (user) {
      form.setFieldsValue({
        userType: UserType.USER,
        description: `
        Name: ${getUserName(user)} 
        Email: ${user.email}
        Phone: ${user.phone}
        `,
      });

      dispatch(chooseUserModalActions.setSelectedUser(user));
    }
  }, [dispatch, form, lead, note, user]);

  const handleSubmit = useCallback(
    async (data: CreateNoteData): Promise<void> => {
      const noteData = { ...data };

      if (selectedUser && (!lead || !user)) {
        if ('leadId' in selectedUser) noteData.leadId = selectedUser.leadId;

        if ('userId' in selectedUser && selectedUser.userId) noteData.userId = selectedUser.userId;
      }

      try {
        switch (mode) {
          case DrawerMode.NOTE_CREATE:
            await createNote({
              ...noteData,
              deadlineDate: data.deadlineDate && getServerDateWithMinuteTimeFormat(data.deadlineDate),
            }).unwrap();
            showNotification('info', t('Success', { ns: 'common' }), t('Note has been successfully created'));
            break;
          case DrawerMode.NOTE_EDIT:
            if (note) {
              const { noteId } = note;
              await updateNote({
                ...noteData,
                noteId,
                deadlineDate: data.deadlineDate && getServerDateWithMinuteTimeFormat(data.deadlineDate),
                description: `
                ${t('Note changed by')} ${getUserName(loggedUser)}
                ${data.description || ''}
                `,
              }).unwrap();
              showNotification('info', t('Success', { ns: 'common' }), t('Note has been successfully updated'));
            }
            break;
        }

        form.resetFields();

        onClose();
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error', { ns: 'common' }), t(`Error when ${!isNoteEditMode ? 'creating' : 'updating'} note`));
      }
    },
    [selectedUser, lead, user, mode, form, onClose, createNote, t, note, updateNote, loggedUser, isNoteEditMode],
  );

  const disableDate = useCallback((currentDate: Dayjs): boolean => {
    const today = dayjs().startOf('day');

    return currentDate.isBefore(today, 'day');
  }, []);

  const paragraphs = note?.description.split('\n');

  const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
    <p key={index} className="font-normal !mb-1">
      {paragraph}
    </p>
  ));

  const warehouseOptions = useMemo(() => {
    return (
      warehouses?.map((warehouse) => ({
        label: warehouse.name,
        value: warehouse.warehouseId,
      })) || []
    );
  }, [warehouses]);

  const managersOptions = useMemo(() => {
    return (
      data?.content.map((manager) => ({
        label: `${manager.firstName} ${manager.lastName}`,
        value: manager.userId,
      })) || []
    );
  }, [data?.content]);

  const userTypeOptions = useMemo(
    () => [
      { label: t('Lead'), value: UserType.LEAD },
      { label: t('Customer'), value: UserType.USER },
    ],
    [t],
  );

  const noteTypeOptions = Object.values(NoteType).map((type) => ({
    label: t(type),
    value: type,
  }));

  const changeUserType = useCallback(
    (value: string): void => {
      dispatch(chooseUserModalActions.setUserType(value as UserType));

      if (!value) {
        dispatch(chooseUserModalActions.resetModalState());
      }
    },
    [dispatch],
  );

  const selectedUserInfo = selectedUser?.firstName
    ? `${selectedUser.firstName} ${selectedUser?.lastName || ''}`
    : selectedUser?.email
    ? selectedUser.email
    : selectedUser?.phone;

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4">
      <div className="flex justify-between items-center py-4">
        <div className="font-semibold text-3xl">{t(isNoteEditMode ? 'Edit Note' : 'Create Note')}</div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={onClose}>
          {t('Close')}
        </div>
      </div>

      {isNoteEditMode && (
        <div className="flex items-center mb-3">
          <div className="flex-1">
            <div>{t('Created by')}</div>
            <div className="font-semibold text-xl">
              {note?.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}` : t('System')}
            </div>
          </div>

          <Form.Item name="isCompleted" valuePropName="checked" className="flex-2">
            <Checkbox name="isCompleted" className="mt-6">
              {t('Completed note')}
            </Checkbox>
          </Form.Item>
        </div>
      )}

      <Form.Item className="w-full" name="deadlineDate" label={t('Deadline date')}>
        <DatePicker
          className="h-[52px] w-full"
          format={CLIENT_DATE_WITH_MINUTE_TIME_FORMAT}
          placeholder={t('Enter deadline date')}
          inputReadOnly
          disabledDate={disableDate}
          showTime
        />
      </Form.Item>

      <Form.Item label={t('Assigned to')} name="assignedIds">
        <MultipleSelect placeholder={t('Please, assign note')} bordered options={managersOptions} allowClear />
      </Form.Item>

      <Form.Item label={t('Warehouse')} name="warehouseId">
        <Select options={warehouseOptions} placeholder={t('Please, select warehouse')} bordered allowClear />
      </Form.Item>

      <Form.Item label={t('Type')} name="noteType" rules={[{ required: true, message: `${t('Please, select type')}!` }]}>
        <Select options={noteTypeOptions} placeholder={t('Please, select type')} bordered allowClear />
      </Form.Item>

      <Form.Item label={t('User type')} name="userType">
        <Select options={userTypeOptions} onChange={changeUserType} placeholder={t('Please, select user type')} bordered allowClear />
      </Form.Item>

      {selectedUser && (
        <Form.Item label={t('Selected user')}>
          <Input value={selectedUserInfo} bordered />
        </Form.Item>
      )}

      <Form.Item
        label={t('Description')}
        name="description"
        rules={[{ required: !isNoteEditMode, message: `${t('Please, enter description')}!` }]}
      >
        <TextArea placeholder={t('Add description...')} bordered rows={3} />
      </Form.Item>

      <Button type="submit" isLoading={isLoading}>
        {t('Save', { ns: 'common' })}
      </Button>

      {isNoteEditMode && (
        <div className="mt-6">
          <div className="mb-2">{t('Descriptions history')}:</div>
          <Paragraph expandable={false} className="whitespace-normal">
            {paragraphElements}
          </Paragraph>
        </div>
      )}
    </Form>
  );
});
